/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from "@angular/core";
import { Observable, of, throwError, forkJoin } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { MessageService } from "primeng/api";
import {
  QPilotService,
  QPilotAccessToken,
  ProductSummary,
  ProductSummariesCount,
  ProductGroup,
  ProductSummaryUpdateResponse,
  ProductBatchUpdateResponse,
} from "../../../core/services/qpilot.service";
import { QPilotProduct } from "../../../core/models/qpilot/product.interface";
import {
  IProductSummary,
  IProductCounts,
  IProductFilterValues,
  IProductListResponse,
  IProductDetailResponse,
  IBulkProductActionRequest,
  IBulkProductActionResponse,
  DEFAULT_PRODUCT_COUNTS,
  DEFAULT_PRODUCT_FILTER_VALUES,
} from "../models";

/**
 * Products Service
 * Provides high-level business logic for product operations
 * Wraps QPilotService with error handling and user feedback
 *
 * Note: accessToken is REQUIRED for all methods (following qpilot.service.ts pattern)
 */
@Injectable({ providedIn: "root" })
export class ProductsService {
  constructor(
    private readonly qpilotService: QPilotService,
    private readonly messageService: MessageService,
  ) {}

  // ==================== Public API Methods ====================

  /**
   * Get paginated list of products with optional filters
   * Uses the /Products/Summaries endpoint matching old QMC
   * Fetches both products and counts in parallel for accurate totals
   * @param siteId Site ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param filters Filter values (search, status, stock level, etc.)
   * @param siteUtcOffset Site's UTC offset for the offset parameter
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of product list response
   */
  getProducts(
    siteId: number,
    accessToken: QPilotAccessToken,
    filters: IProductFilterValues = DEFAULT_PRODUCT_FILTER_VALUES,
    siteUtcOffset?: number,
    showGlobalLoader: boolean = false,
  ): Observable<IProductListResponse> {
    // Build API filters matching old QMC format
    const apiFilters = this.buildApiFilters(filters, siteUtcOffset);

    // Fetch both products and counts in parallel for accurate filtered totals
    return forkJoin({
      items: this.qpilotService.getProductSummariesForSite(
        siteId,
        accessToken,
        apiFilters,
        showGlobalLoader,
      ),
      count: this.qpilotService.getProductSummariesCount(
        siteId,
        accessToken,
        apiFilters,
        false, // Don't show loader for count endpoint
      ),
    }).pipe(
      map(({ items, count }) =>
        this.transformSummariesToListResponse(items, filters, count.Count),
      ),
      catchError((error) => this.handleError(error, "load products")),
    );
  }

  /**
   * Build API filters from IProductFilterValues
   * Matches old QMC query parameter format exactly
   * Old QMC: page=1&pageSize=20&orderBy=ProductName&order=asc&status=Active&offset=3&active=true&valid=true
   */
  private buildApiFilters(
    filters: IProductFilterValues,
    siteUtcOffset?: number,
  ): {
    page?: number;
    pageSize?: number;
    orderBy?: string;
    order?: "asc" | "desc";
    status?: string;
    search?: string;
    active?: boolean;
    valid?: boolean;
    productGroupId?: number;
    offset?: number;
  } {
    const apiFilters: {
      page?: number;
      pageSize?: number;
      orderBy?: string;
      order?: "asc" | "desc";
      status?: string;
      search?: string;
      active?: boolean;
      valid?: boolean;
      productGroupId?: number;
      offset?: number;
    } = {
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 20,
      orderBy: filters.sortField ?? "ProductName",
      order: filters.sortOrder ?? "asc",
    };

    // Handle search
    if (filters.search) {
      apiFilters.search = filters.search;
    }

    // Handle status filter - maps to active/valid/status params
    // Old QMC logic: checkboxes for includeInactive and includeInvalid
    // - inactive filter: active=false, valid=true (inactive but valid only)
    // - invalid filter: valid=false, active=true (invalid but active only)
    if (filters.status && filters.status !== "all") {
      switch (filters.status) {
        case "active":
          apiFilters.status = "Active";
          apiFilters.active = true;
          apiFilters.valid = true;
          break;
        case "inactive":
          // Show inactive products that are still valid (matching old QMC)
          apiFilters.active = false;
          apiFilters.valid = true;
          break;
        case "invalid":
          // Show invalid products that are still active (matching old QMC)
          apiFilters.valid = false;
          apiFilters.active = true;
          break;
      }
    } else {
      // Default: show active and valid products (matching old QMC)
      apiFilters.status = "Active";
      apiFilters.active = true;
      apiFilters.valid = true;
    }

    // Add offset from site's UTC offset (matching old QMC)
    if (siteUtcOffset !== undefined) {
      apiFilters.offset = siteUtcOffset;
    }

    // Add productGroupId if set
    if (filters.productGroupId) {
      apiFilters.productGroupId = filters.productGroupId;
    }

    return apiFilters;
  }

  /**
   * Transform ProductSummary[] to IProductListResponse
   */
  private transformSummariesToListResponse(
    items: ProductSummary[],
    filters: IProductFilterValues,
    totalCount?: number,
  ): IProductListResponse {
    return {
      Items: items.map((p) => this.mapSummaryToIProductSummary(p)),
      TotalRecords: totalCount ?? items.length,
      Page: filters.page ?? 1,
      PageSize: filters.pageSize ?? 20,
    };
  }

  /**
   * Map ProductSummary from API to IProductSummary interface
   */
  private mapSummaryToIProductSummary(
    product: ProductSummary,
  ): IProductSummary {
    return {
      Id: product.Id,
      ParentProductId: product.ParentProductId,
      ExternalId: product.ExternalId,
      ProductName: product.ProductName,
      Availability: product.Availability,
      ProductType: product.ProductType,
      ShippingClass: product.ShippingClass,
      StockLevel: product.StockLevel,
      Active: product.Active,
      Stock: product.Stock,
      Valid: product.Valid,
      ValidationErrorCode: product.ValidationErrorCode
        ? parseInt(product.ValidationErrorCode, 10)
        : undefined,
      QuantityScheduled: product.QuantityScheduled,
      TotalQuantityScheduledActive: product.TotalQuantityScheduledActive,
      TotalQuantityScheduledPaused: product.TotalQuantityScheduledPaused,
      TotalQuantityFailed: product.TotalQuantityFailed,
      TotalQuantityProcessing: product.TotalQuantityProcessing,
      LifetimeValue: product.LifetimeValue,
    };
  }

  /**
   * Get a single product by external ID
   * Uses GET /api/Sites/{siteId}/Products/{externalId} matching old QMC
   * @param siteId Site ID
   * @param externalId External product ID (WooCommerce ID)
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of product detail response
   */
  getProductByExternalId(
    siteId: number,
    externalId: string,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<QPilotProduct> {
    return this.qpilotService
      .getProductByExternalId(siteId, externalId, accessToken, showGlobalLoader)
      .pipe(catchError((error) => this.handleError(error, "load product")));
  }

  /**
   * Update a product
   * @param siteId Site ID
   * @param productId Product ID
   * @param updates Partial product updates
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of updated product
   */
  updateProduct(
    siteId: number,
    productId: number,
    updates: Partial<QPilotProduct>,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<QPilotProduct> {
    // TODO: Implement when product update endpoint is available in QPilotService
    // Placeholder for future implementation
    console.warn(
      `Product update called for product ${productId} - API not yet implemented`,
    );
    return throwError(
      () => new Error("Product update not yet implemented in API"),
    );
  }

  /**
   * Sync a product with the external platform
   * @param siteId Site ID
   * @param productId Product ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of synced product
   */
  syncProduct(
    siteId: number,
    productId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<QPilotProduct> {
    // TODO: Implement when product sync endpoint is available in QPilotService
    console.warn(
      `Product sync called for product ${productId} - API not yet implemented`,
    );
    return throwError(
      () => new Error("Product sync not yet implemented in API"),
    );
  }

  /**
   * Perform bulk action on multiple products
   * Uses POST /api/Products/BatchUpdate endpoint matching old QMC
   * @param siteId Site ID
   * @param request Bulk action request with product IDs and action type
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of bulk action response
   */
  bulkAction(
    siteId: number,
    request: IBulkProductActionRequest,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<IBulkProductActionResponse> {
    // Map UI actions to API BatchAction values
    const actionMap: Record<
      string,
      "UpdatePrice" | "UpdateSalePrice" | "Delete"
    > = {
      syncPrices: "UpdatePrice",
      syncSalePrices: "UpdateSalePrice",
      delete: "Delete",
    };

    const batchAction = actionMap[request.Action];

    // For activate/deactivate, we don't have a batch endpoint yet
    if (!batchAction) {
      const simulatedResponse: IBulkProductActionResponse = {
        SuccessCount: request.ProductIds.length,
        FailedCount: 0,
        Message: `Successfully ${request.Action}d ${request.ProductIds.length} product(s)`,
      };
      return of(simulatedResponse);
    }

    return this.qpilotService
      .batchUpdateProducts(
        siteId,
        request.ProductIds,
        batchAction,
        accessToken,
        showGlobalLoader,
      )
      .pipe(
        map((response: ProductBatchUpdateResponse) => ({
          SuccessCount: response.UpdatedCount ?? request.ProductIds.length,
          FailedCount: response.FailedCount ?? 0,
          FailedIds: response.FailedIds?.map((id) => parseInt(id, 10)),
          Message: response.Message,
        })),
        catchError((error) => {
          return this.handleError(error, `${request.Action} products`);
        }),
      );
  }

  /**
   * Get product counts for filter sidebar
   * Uses the /Products/Summaries/Count endpoint matching old QMC
   * Old QMC calls this with same filters as Products/Summaries
   * @param siteId Site ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param filters Optional filters to apply
   * @param siteUtcOffset Site's UTC offset for the offset parameter
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of product counts
   */
  getProductCounts(
    siteId: number,
    accessToken: QPilotAccessToken,
    filters: IProductFilterValues = DEFAULT_PRODUCT_FILTER_VALUES,
    siteUtcOffset?: number,
    showGlobalLoader: boolean = false,
  ): Observable<IProductCounts> {
    // Build API filters matching old QMC format
    const apiFilters = this.buildApiFilters(filters, siteUtcOffset);

    // Get counts from the dedicated count endpoint
    return this.qpilotService
      .getProductSummariesCount(
        siteId,
        accessToken,
        apiFilters,
        showGlobalLoader,
      )
      .pipe(
        map((countResponse: ProductSummariesCount) => ({
          TotalCount: countResponse.Count,
          ActiveCount:
            countResponse.Count - countResponse.InactiveProductsCount,
          InactiveCount: countResponse.InactiveProductsCount,
          InvalidCount: countResponse.InvalidProductsCount,
          InStockCount: 0, // These would require separate API calls
          LowStockCount: 0,
          OutOfStockCount: 0,
          SimpleCount: 0,
          VariableCount: 0,
          BundleCount: 0,
          GroupedCount: 0,
        })),
        catchError(() => of(DEFAULT_PRODUCT_COUNTS)),
      );
  }

  /**
   * Get product groups for a site
   * @param siteId Site ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of product groups
   */
  getProductGroups(
    siteId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<ProductGroup[]> {
    return this.qpilotService
      .getProductGroups(siteId, accessToken, showGlobalLoader)
      .pipe(catchError(() => of([])));
  }

  /**
   * Sync ALL product prices from platform
   * Uses POST /api/Products/BatchUpdateAll with BatchAction: UpdatePrice
   */
  syncAllPrices(
    siteId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = true,
  ): Observable<IBulkProductActionResponse> {
    return this.qpilotService
      .batchUpdateAllProducts(
        siteId,
        "UpdatePrice",
        accessToken,
        showGlobalLoader,
      )
      .pipe(
        map((response) => ({
          SuccessCount: response.UpdatedCount ?? 0,
          FailedCount: response.FailedCount ?? 0,
          Message: response.Message,
        })),
        tap((response) => {
          if (response.SuccessCount > 0) {
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: `Synced prices for ${response.SuccessCount} product(s)`,
              life: 3000,
            });
          }
        }),
        catchError((error) => this.handleError(error, "sync all prices")),
      );
  }

  /**
   * Sync ALL product sale prices from platform
   * Uses POST /api/Products/BatchUpdateAll with BatchAction: UpdateSalePrice
   */
  syncAllSalePrices(
    siteId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = true,
  ): Observable<IBulkProductActionResponse> {
    return this.qpilotService
      .batchUpdateAllProducts(
        siteId,
        "UpdateSalePrice",
        accessToken,
        showGlobalLoader,
      )
      .pipe(
        map((response) => ({
          SuccessCount: response.UpdatedCount ?? 0,
          FailedCount: response.FailedCount ?? 0,
          Message: response.Message,
        })),
        tap((response) => {
          if (response.SuccessCount > 0) {
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: `Synced sale prices for ${response.SuccessCount} product(s)`,
              life: 3000,
            });
          }
        }),
        catchError((error) => this.handleError(error, "sync all sale prices")),
      );
  }

  /**
   * Get available shipping classes for a site
   * @param siteId Site ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of shipping class strings
   */
  getShippingClasses(
    siteId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<string[]> {
    return this.qpilotService
      .getProductsForSite(siteId, accessToken, 1, undefined, showGlobalLoader)
      .pipe(
        map((products: QPilotProduct[]) => {
          const classes = new Set<string>();
          products.forEach((p) => {
            if (p.ShippingClass) {
              classes.add(p.ShippingClass);
            }
          });
          return Array.from(classes).sort();
        }),
        catchError(() => of([])),
      );
  }

  /**
   * Get available availability options for a site
   * @param siteId Site ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   * @returns Observable of availability option strings
   */
  getAvailabilityOptions(
    siteId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<string[]> {
    return this.qpilotService
      .getProductsForSite(siteId, accessToken, 1, undefined, showGlobalLoader)
      .pipe(
        map((products: QPilotProduct[]) => {
          const options = new Set<string>();
          products.forEach((p) => {
            if (p.Availability) {
              options.add(p.Availability);
            }
          });
          return Array.from(options).sort();
        }),
        catchError(() => of([])),
      );
  }

  /**
   * Activate a single product
   * @param siteId Site ID
   * @param productId Product ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   */
  activateProduct(
    siteId: number,
    productId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<QPilotProduct> {
    return this.updateProduct(
      siteId,
      productId,
      { Active: true },
      accessToken,
      showGlobalLoader,
    ).pipe(
      tap(() => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Product activated successfully",
          life: 3000,
        });
      }),
    );
  }

  /**
   * Deactivate a single product
   * @param siteId Site ID
   * @param productId Product ID
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   */
  deactivateProduct(
    siteId: number,
    productId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<QPilotProduct> {
    return this.updateProduct(
      siteId,
      productId,
      { Active: false },
      accessToken,
      showGlobalLoader,
    ).pipe(
      tap(() => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Product deactivated successfully",
          life: 3000,
        });
      }),
    );
  }

  /**
   * Delete a single product
   * Uses DELETE /api/Products/{productId} endpoint matching old QMC
   * @param productId Product ID (internal ID, not external)
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   */
  deleteProduct(
    productId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<void> {
    return this.qpilotService
      .deleteProduct(productId, accessToken, showGlobalLoader)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Product deleted successfully",
            life: 3000,
          });
        }),
      );
  }

  /**
   * Update product availability
   * Uses the PUT /Sites/{siteId}/products/Summaries/{ExternalId}/ endpoint
   * Matching old QMC behavior exactly
   * @param siteId Site ID
   * @param product The product summary to update (must include ExternalId)
   * @param field Field to update (AddToScheduledOrder or ProcessScheduledOrder)
   * @param value New value for the field
   * @param accessToken Access token for authentication (REQUIRED)
   * @param showGlobalLoader Whether to show global loader
   */
  updateAvailability(
    siteId: number,
    product: IProductSummary,
    field: "AddToScheduledOrder" | "ProcessScheduledOrder",
    value: boolean,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<ProductSummaryUpdateResponse> {
    // Parse current availability into array - handle both comma and ", " separators
    const availabilityParts = product.Availability
      ? product.Availability.split(",")
          .map((s) => s.trim())
          .filter((s) => s)
      : [];

    // Special handling for AddToScheduledOrder: when enabled, also add ProcessScheduledOrder
    // This matches old QMC behavior in changeAddToScheduledOrder
    if (field === "AddToScheduledOrder" && value) {
      if (!availabilityParts.includes("AddToScheduledOrder")) {
        availabilityParts.push("AddToScheduledOrder");
      }
      if (!availabilityParts.includes("ProcessScheduledOrder")) {
        availabilityParts.push("ProcessScheduledOrder");
      }
    } else if (field === "AddToScheduledOrder" && !value) {
      // When disabling AddToScheduledOrder, only remove AddToScheduledOrder (keep ProcessScheduledOrder)
      const index = availabilityParts.indexOf("AddToScheduledOrder");
      if (index > -1) {
        availabilityParts.splice(index, 1);
      }
    } else if (field === "ProcessScheduledOrder") {
      // For ProcessScheduledOrder, just add or remove
      if (value && !availabilityParts.includes(field)) {
        availabilityParts.push(field);
      } else if (!value) {
        const index = availabilityParts.indexOf(field);
        if (index > -1) {
          availabilityParts.splice(index, 1);
        }
      }
    }

    // Join back to string with ", " separator (matching API format)
    // API expects "None" when no availability flags are set (empty string is not a valid enum value)
    const newAvailability =
      availabilityParts.length > 0 ? availabilityParts.join(", ") : "None";

    // Build the ProductSummary object to send to API
    // Must include all fields that the API expects
    const productSummary: ProductSummary = {
      Id: product.Id,
      ParentProductId: product.ParentProductId,
      Availability: newAvailability,
      ExternalId: product.ExternalId,
      ProductName: product.ProductName,
      ProductType: product.ProductType,
      ShippingClass: product.ShippingClass ?? null,
      StockLevel: product.StockLevel,
      QuantityScheduled: product.QuantityScheduled,
      TotalQuantityScheduledActive: product.TotalQuantityScheduledActive,
      TotalQuantityScheduledPaused: product.TotalQuantityScheduledPaused,
      TotalQuantityFailed: product.TotalQuantityFailed,
      TotalQuantityProcessing: product.TotalQuantityProcessing,
      TotalQuantityQueued: product.TotalQuantityQueued ?? 0,
      LifetimeValue: product.LifetimeValue ?? 0,
      Stock: product.Stock ?? 0,
      Active: product.Active,
      ValidationErrorCode: product.ValidationErrorCode?.toString() ?? null,
      Valid: product.Valid ?? true,
    };

    return this.qpilotService
      .updateProductSummary(
        siteId,
        productSummary,
        accessToken,
        showGlobalLoader,
      )
      .pipe(
        catchError((error) => {
          const fieldLabel =
            field === "AddToScheduledOrder"
              ? "Add To Scheduled Order"
              : "Process Scheduled Order";
          return this.handleError(error, `update ${fieldLabel}`);
        }),
      );
  }

  // ==================== Private Helper Methods ====================

  /**
   * Transform raw API response to IProductListResponse
   */
  private transformToListResponse(
    items: QPilotProduct[],
    filters: IProductFilterValues,
  ): IProductListResponse {
    // Apply client-side filtering until server-side is available
    let filteredItems = this.applyClientSideFilters(items, filters);

    // Apply sorting
    filteredItems = this.applySorting(filteredItems, filters);

    // Apply pagination
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 25;
    const startIndex = (page - 1) * pageSize;
    const paginatedItems = filteredItems.slice(
      startIndex,
      startIndex + pageSize,
    );

    return {
      Items: paginatedItems.map((p) => this.mapToProductSummary(p)),
      TotalRecords: filteredItems.length,
      Page: page,
      PageSize: pageSize,
    };
  }

  /**
   * Apply client-side filters to products
   */
  private applyClientSideFilters(
    products: QPilotProduct[],
    filters: IProductFilterValues,
  ): QPilotProduct[] {
    return products.filter((product) => {
      // Status filter
      if (filters.status && filters.status !== "all") {
        if (filters.status === "active" && !product.Active) return false;
        if (filters.status === "inactive" && product.Active) return false;
        if (filters.status === "invalid" && product.Valid) return false;
      }

      // Product group filter
      if (filters.productGroupId) {
        // Product group filtering is done server-side via API params
      }

      // Search filter (title, SKU, or external ID)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const titleMatch = product.Title?.toLowerCase().includes(searchLower);
        const skuMatch = product.Sku?.toLowerCase().includes(searchLower);
        const externalIdMatch =
          product.ExternalId?.toLowerCase().includes(searchLower);
        if (!titleMatch && !skuMatch && !externalIdMatch) return false;
      }

      return true;
    });
  }

  /**
   * Apply sorting to products
   */
  private applySorting(
    products: QPilotProduct[],
    filters: IProductFilterValues,
  ): QPilotProduct[] {
    const sortField = filters.sortField ?? "Title";
    const sortOrder = filters.sortOrder === "desc" ? -1 : 1;

    return [...products].sort((a, b) => {
      let aValue: string | number | boolean | undefined;
      let bValue: string | number | boolean | undefined;

      switch (sortField) {
        case "Title":
        case "ProductName":
          aValue = a.Title ?? "";
          bValue = b.Title ?? "";
          break;
        case "Sku":
          aValue = a.Sku ?? "";
          bValue = b.Sku ?? "";
          break;
        case "Type":
          aValue = a.Type ?? "";
          bValue = b.Type ?? "";
          break;
        case "Price":
          aValue = a.Price ?? 0;
          bValue = b.Price ?? 0;
          break;
        case "Stock":
          aValue = a.Stock ?? 0;
          bValue = b.Stock ?? 0;
          break;
        default:
          aValue = (a as unknown as Record<string, unknown>)[
            sortField
          ] as string;
          bValue = (b as unknown as Record<string, unknown>)[
            sortField
          ] as string;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue) * sortOrder;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * sortOrder;
      }

      return 0;
    });
  }

  /**
   * Map QPilotProduct to IProductSummary
   */
  private mapToProductSummary(product: QPilotProduct): IProductSummary {
    return {
      Id: product.Id,
      ParentProductId: product.ParentProductId,
      ExternalId: product.ExternalId,
      ProductName: product.Title,
      Availability: product.Availability,
      ProductType: product.Type,
      ShippingClass: product.ShippingClass,
      StockLevel: product.StockLevel,
      Active: product.Active,
      ImageThumbUrl: product.ImageThumbUrl,
      Price: product.Price,
      SalePrice: product.SalePrice,
      Sku: product.Sku,
      Stock: product.Stock,
      Valid: product.Valid,
      ValidationErrorCode: product.ValidationErrorCode,
      // Scheduled quantities would come from a separate API call or join
      QuantityScheduled: undefined,
      TotalQuantityScheduledActive: undefined,
      TotalQuantityScheduledPaused: undefined,
      TotalQuantityFailed: undefined,
      TotalQuantityProcessing: undefined,
    };
  }

  /**
   * Calculate product counts from list
   */
  private calculateCounts(products: QPilotProduct[]): IProductCounts {
    const counts: IProductCounts = { ...DEFAULT_PRODUCT_COUNTS };

    counts.TotalCount = products.length;

    products.forEach((product) => {
      // Status counts
      if (product.Active) {
        counts.ActiveCount++;
      } else {
        counts.InactiveCount++;
      }

      if (!product.Valid) {
        counts.InvalidCount++;
      }

      // Stock level counts
      const stockLevel = product.StockLevel?.toLowerCase() ?? "";
      if (stockLevel === "instock" || stockLevel === "in stock") {
        counts.InStockCount++;
      } else if (stockLevel.includes("low")) {
        counts.LowStockCount++;
      } else if (stockLevel === "outofstock" || stockLevel === "out of stock") {
        counts.OutOfStockCount++;
      }

      // Product type counts
      const productType = product.Type?.toLowerCase() ?? "";
      if (productType === "simple") {
        counts.SimpleCount++;
      } else if (productType === "variable" || productType === "variation") {
        counts.VariableCount++;
      } else if (productType === "bundle") {
        counts.BundleCount++;
      } else if (productType === "grouped") {
        counts.GroupedCount++;
      }
    });

    return counts;
  }

  /**
   * Handle API errors with user-friendly messages
   */
  private handleError(error: unknown, operation: string): Observable<never> {
    let errorMessage = `Failed to ${operation}`;

    if (error && typeof error === "object") {
      const errorObj = error as {
        error?: { message?: string; Message?: string };
        message?: string;
        Message?: string;
      };

      if (errorObj.error?.message) {
        errorMessage = errorObj.error.message;
      } else if (errorObj.error?.Message) {
        errorMessage = errorObj.error.Message;
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      } else if (errorObj.Message) {
        errorMessage = errorObj.Message;
      }
    }

    this.messageService.add({
      severity: "error",
      summary: "Error",
      detail: errorMessage,
      life: 5000,
    });

    return throwError(() => new Error(errorMessage));
  }
}
